/**
 * Endereços e vocabulário das telas de contrato.
 *
 * Mora separado das telas pelo mesmo motivo de `app/nav.ts` e de
 * `features/clientes/navegacao.ts`: o roteador também precisa dos caminhos, e
 * duas listas de rota divergem no primeiro ajuste — o sintoma é um item de
 * menu que leva a 404.
 */

import type { ContractDirection, ContractStatus, ContractType, Coverage, TermStatus } from '@/features/contratos/contratos.api';

/**
 * Literais, e não `string`.
 *
 * Enquanto as rotas não estavam penduradas em `app/router.tsx`, o tipo largo
 * era o que deixava a tela compilar antes da integração. Agora que elas estão,
 * o literal é o que faz o TanStack reconhecer `/contratos` na união de destinos
 * — e é ele que transforma um item de menu sem rota registrada em erro de
 * compilação, em vez de 404 na cara de quem clicou.
 */
export const CAMINHO_LISTA = '/contratos';

/** O parâmetro do TanStack. O caminho montado fica em `enderecoDoContrato`. */
export const CAMINHO_DETALHE = '/contratos/$contratoId';

/**
 * O endereço da ficha, já montado.
 *
 * Devolve o **template** e os parâmetros separados, e não uma string pronta:
 * é assim que o `<Link>` do TanStack casa o destino com a rota registrada. Uma
 * string interpolada seria só texto para ele, e a checagem de destino — a que
 * pega o caminho que deixou de existir — não aconteceria.
 */
export const enderecoDoContrato = (id: string) =>
  ({ to: CAMINHO_DETALHE, params: { contratoId: id } }) as const;

export type Tom = 'neutro' | 'sucesso' | 'alerta' | 'perigo' | 'info';

/**
 * As seis situações do contrato — **derivadas das datas**, nunca digitadas.
 *
 * O texto de cada uma diz de onde ela vem, e não é decoração: no Topdesk, o
 * campo equivalente é mantido à mão e 32 dos 63 contratos com situação dizem
 * "Vigente" e já venceram. Quem abre esta tela precisa saber que aqui o número
 * não mente porque ninguém o digita.
 */
export const SITUACOES_DE_CONTRATO: ReadonlyArray<{
  valor: ContractStatus;
  rotulo: string;
  tom: Tom;
  origem: string;
}> = [
  {
    valor: 'DRAFT',
    rotulo: 'Minuta',
    tom: 'neutro',
    origem: 'Nenhum termo entrou em vigor ainda. O contrato existe, mas não governa nada.',
  },
  {
    valor: 'SCHEDULED',
    rotulo: 'Programado',
    tom: 'info',
    origem:
      'O termo já foi ativado, mas a vigência dele começa adiante. O contrato não venceu — ele ainda não entrou em vigor.',
  },
  {
    valor: 'ACTIVE',
    rotulo: 'Vigente',
    tom: 'sucesso',
    origem: 'Há um termo em vigor hoje. Derivado das datas do termo, não de um campo digitado.',
  },
  {
    valor: 'SUSPENDED',
    rotulo: 'Suspenso',
    tom: 'alerta',
    origem:
      'Alguém suspendeu, com data e motivo. Chamado novo passa a nascer fora de termo; o que já está aberto continua como está.',
  },
  {
    valor: 'EXPIRED',
    rotulo: 'Vencido',
    tom: 'perigo',
    origem: 'O último termo acabou e nenhum outro entrou. A data manda, não o que está escrito.',
  },
  {
    valor: 'TERMINATED',
    rotulo: 'Rescindido',
    tom: 'perigo',
    origem: 'Rescisão registrada com data e motivo. Nada foi apagado: o histórico continua inteiro.',
  },
] as const;

export const situacaoDeContrato = (valor: ContractStatus) =>
  SITUACOES_DE_CONTRATO.find((s) => s.valor === valor) ?? SITUACOES_DE_CONTRATO[0]!;

/**
 * Os dez tipos. `INTERNAL_OLA` é o que mais se erra ao ler, e por isso o texto
 * dele diz o que ele faz e não o que ele é.
 */
export const TIPOS_DE_CONTRATO: ReadonlyArray<{
  valor: ContractType;
  rotulo: string;
  nota?: string;
}> = [
  { valor: 'MANAGED_SERVICE', rotulo: 'Serviço gerenciado' },
  { valor: 'INFRASTRUCTURE', rotulo: 'Infraestrutura de TI' },
  { valor: 'RENTAL', rotulo: 'Locação' },
  { valor: 'PURCHASE', rotulo: 'Aquisição' },
  { valor: 'MAINTENANCE', rotulo: 'Manutenção' },
  { valor: 'PROJECT', rotulo: 'Projeto' },
  { valor: 'SUPPORT_HOURS', rotulo: 'Pacote de horas' },
  { valor: 'WARRANTY', rotulo: 'Garantia' },
  {
    valor: 'UNDERPINNING',
    rotulo: 'Contrato de apoio (UC)',
    nota: 'Com fornecedor externo. Sustenta um SLA por baixo.',
  },
  {
    valor: 'INTERNAL_OLA',
    rotulo: 'Acordo interno (OLA)',
    nota: 'Entre áreas da própria Aptum. Nunca aparece no portal do cliente.',
  },
  {
    valor: 'PJ_SERVICES',
    rotulo: 'Serviços PJ',
    nota: 'Contrato de prestação de serviços por pessoa jurídica. Apenas INBOUND.',
  },
  {
    valor: 'SUPPLIER',
    rotulo: 'Fornecedor',
    nota: 'Fornecimento de produto ou licença por empresa externa. Apenas INBOUND.',
  },
] as const;

export const DIRECAO: Record<ContractDirection, { rotulo: string; tom: Tom }> = {
  OUTBOUND: { rotulo: 'Para o cliente', tom: 'info' },
  INBOUND:  { rotulo: 'PJ / Fornecedor', tom: 'alerta' },
};

export const direcaoDeContrato = (d: ContractDirection) => DIRECAO[d];

export const tipoDeContrato = (valor: ContractType) =>
  TIPOS_DE_CONTRATO.find((t) => t.valor === valor) ?? TIPOS_DE_CONTRATO[0]!;

/** As quatro situações de um termo. */
export const SITUACOES_DE_TERMO: ReadonlyArray<{
  valor: TermStatus;
  rotulo: string;
  tom: Tom;
  efeito: string;
}> = [
  {
    valor: 'DRAFT',
    rotulo: 'Rascunho',
    tom: 'neutro',
    efeito: 'Ainda editável e sem efeito nenhum. Só um rascunho pode ter serviços e escopo alterados.',
  },
  {
    valor: 'ACTIVE',
    rotulo: 'Ativo',
    tom: 'sucesso',
    efeito: 'Em vigor e imutável: renovar ou aditar cria um termo novo, e este fica como está.',
  },
  {
    valor: 'SUPERSEDED',
    rotulo: 'Substituído',
    tom: 'info',
    efeito: 'Um termo posterior tomou o lugar dele. Continua consultável — é a resposta de "qual era o ANS em março".',
  },
  {
    valor: 'EXPIRED',
    rotulo: 'Encerrado',
    tom: 'neutro',
    efeito: 'A vigência acabou e o sistema o encerrou na varredura diária.',
  },
] as const;

export const situacaoDeTermo = (valor: TermStatus) =>
  SITUACOES_DE_TERMO.find((s) => s.valor === valor) ?? SITUACOES_DE_TERMO[0]!;

/**
 * As cinco coberturas de um chamado.
 *
 * `NOT_APPLICABLE` **não** quer dizer "chamado interno": 21 dos 98 contratos
 * reais são acordos internos da Aptum, e 211 chamados internos nascem sob um
 * deles. Ele quer dizer "não há acordo aplicável", e o texto diz isso.
 */
export const COBERTURAS: ReadonlyArray<{
  valor: Coverage;
  rotulo: string;
  tom: Tom;
  efeito: string;
}> = [
  {
    valor: 'COVERED',
    rotulo: 'Coberto',
    tom: 'sucesso',
    efeito: 'Termo vigente, serviço coberto, parque no escopo. Conta na aderência do cliente.',
  },
  {
    valor: 'OUT_OF_TERM',
    rotulo: 'Fora de vigência',
    tom: 'alerta',
    efeito:
      'Abriu sem termo em vigor. Recebe prazo não contratual e NÃO conta na aderência do cliente.',
  },
  {
    valor: 'OUT_OF_SCOPE',
    rotulo: 'Fora de escopo',
    tom: 'alerta',
    efeito: 'Havia termo vigente, mas o serviço ou o item de configuração não está coberto.',
  },
  {
    valor: 'BILLABLE',
    rotulo: 'Excedente',
    tom: 'info',
    efeito: 'A franquia do período acabou. O atendimento seguiu; o trabalho entra como excedente.',
  },
  {
    valor: 'NOT_APPLICABLE',
    rotulo: 'Sem acordo',
    tom: 'neutro',
    efeito: 'Nenhum contrato governa este chamado — não é o mesmo que "chamado interno".',
  },
] as const;

export const cobertura = (valor: Coverage) =>
  COBERTURAS.find((c) => c.valor === valor) ?? COBERTURAS[4]!;

/**
 * O texto do vencimento, com o sinal certo.
 *
 * Nulo é ambíguo por natureza — pode ser "sem termo em vigor" ou "prazo
 * indeterminado" —, e a tela não inventa um número para ele.
 */
export function textoDeVencimento(dias: number | null, temTermo: boolean): string {
  if (dias === null) return temTermo ? 'Prazo indeterminado' : 'Sem termo em vigor';
  if (dias < 0) return `Venceu há ${Math.abs(dias)} dia(s)`;
  if (dias === 0) return 'Vence hoje';
  return `Vence em ${dias} dia(s)`;
}

/** Vermelho a partir do vencimento, âmbar dentro da janela de aviso padrão. */
export function tomDeVencimento(dias: number | null): Tom {
  if (dias === null) return 'neutro';
  if (dias < 0) return 'perigo';
  if (dias <= 60) return 'alerta';
  return 'sucesso';
}
